import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    Grid,
    GridProps,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { AppSearchBar } from "./AppSearchBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppFiltersDialog } from "./AppFiltersDialog";
import AppMultiTagSelect, { MultiAutocompleteProps } from "./AppMultiTagSelect";
import { AppButton } from "./AppButton";
import { ContentAttribute } from "JS/Models/Attributes";

export interface AppHeaderProps extends InternalStandardProps<GridProps> {
    title: string;
    searchPlaceholder?: string;
    searchQuery?: string;
    canGoBack?: boolean;
    searchable?: boolean;
    showTabs?: boolean;
    tabs?: any;
    selectedTab?: string;
    setSelectedTab?: (val: string) => void;
    setSearchQuery?: (val: string) => void;
    onBackClick?: () => void;
    onSearchFieldClick?: () => void;
    filterable?: boolean;
    uniqueSpeakers?: ContentAttribute[];
    onToggleSpeakerSelect?: MultiAutocompleteProps["onChange"];
    selectedSpeakers?: string[];
    handleClearFilters?: () => void;
    resetPagination?: () => void;
}

const HeadingItem = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey.A100,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const Item = styled("div")(({ theme }) => ({
    padding: theme.spacing(2),
    paddingTop: `calc(${theme.spacing(2)} + env(safe-area-inset-top, 0px))`,
}));

const SearchItem = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(2, 0),
    },
}));

const TitleContainer = styled("div")({
    display: "flex",
    alignItems: "center",
});

const BackIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.grey.A100,
    fontSize: "20px",
    marginRight: theme.spacing(2),
}));

const Tab = styled("span")<{ selected?: boolean }>(({ theme, selected }) => ({
    cursor: "pointer",
    fontSize: "1rem",
    color: selected ? theme.colors?.white : theme.palette.grey[800],
    fontWeight: 500,
    border: `2px solid ${selected ? theme.palette.grey[900] : theme.palette.grey[800]}`,
    padding: "4px 28px",
    borderRadius: "25px",
    ...(selected && {
        backgroundColor: theme.palette.grey[900],
    }),
}));

const FilterIcon = styled(FontAwesomeIcon)<{ selected?: boolean }>(
    ({ theme, selected }) => ({
        fontSize: "26px",
        cursor: "pointer",
        color: selected
            ? `${theme.palette.primary.main} !important`
            : theme.palette.grey[600],
    }),
);

const FilterCounter = styled(Box)(({ theme }) => ({
    position: "absolute",
    minHeight: "22px",
    minWidth: "22px",
    height: "auto",
    width: "auto",
    backgroundColor: theme.palette.primary.main,
    color: theme.colors?.white,
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "-16px 0px 10px 15px",
    fontSize: "12px",
    fontWeight: "500",
}));

const BtnClearWrapper = styled(Grid)({
    display: "flex",
    justifyContent: "end",
    alignItems: "center",
    padding: "16px 0 16px 16px",
});

const SpeakerFilterBtn = styled(AppButton)(({ theme }) => ({
    "&:focus": {
        backgroundColor: theme.palette.primary.main,
    },
    "&:active": {
        backgroundColor: theme.palette.primary.main,
    },
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
    },
}));

export const AppHeader = (props: AppHeaderProps) => {
    const {
        className,
        searchQuery,
        searchPlaceholder,
        title,
        canGoBack,
        searchable = true,
        filterable = false,
        tabs,
        showTabs,
        selectedTab,
        setSearchQuery,
        onBackClick = () => {},
        setSelectedTab,
        onSearchFieldClick = () => {},
        uniqueSpeakers,
        onToggleSpeakerSelect,
        selectedSpeakers,
        handleClearFilters,
        resetPagination,
        ...rest
    } = props;

    const { filterDialogOpen, toogleFilterDialog } = useHeaderHandlers();

    const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            toogleFilterDialog();
        }
    };

    return (
        <Grid {...rest} container alignItems={"center"}>
            <Grid item md={6} xs={12}>
                <TitleContainer
                    as={Item}
                    onClick={onBackClick}
                    sx={
                        canGoBack
                            ? { cursor: "pointer", transition: "0.2s ease-in" }
                            : undefined
                    }
                >
                    {canGoBack && <BackIcon icon={["fas", "chevron-left"]} />}
                    <HeadingItem fontWeight={"bold"} variant="h5">
                        {title}
                    </HeadingItem>
                </TitleContainer>
            </Grid>
            {showTabs && (
                <Box
                    marginTop={"22px"}
                    width={"100%"}
                    display={"flex"}
                    justifyContent={"space-around"}
                >
                    {Object.values(tabs).map((tab: string) => (
                        <Tab
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            selected={selectedTab === tab}
                        >
                            {tab}
                        </Tab>
                    ))}
                </Box>
            )}
            {searchable && (
                <>
                    <SearchItem
                        item
                        md={filterable ? 5 : 6}
                        xs={filterable ? 11 : 12}
                    >
                        <AppSearchBar
                            sx={
                                !searchable
                                    ? { visibility: "hidden" }
                                    : undefined
                            }
                            value={searchQuery}
                            onChange={(e) => {
                                if (setSearchQuery) {
                                    setSearchQuery(e.target.value);
                                }
                            }}
                            placeholder={searchPlaceholder}
                            onCancel={() => {
                                if (setSearchQuery) {
                                    setSearchQuery("");
                                }
                            }}
                            onClick={onSearchFieldClick}
                        />
                    </SearchItem>
                    {filterable && (
                        <>
                            <SearchItem item md={1} xs={1}>
                                {selectedSpeakers?.length > 0 && (
                                    <FilterCounter>
                                        {selectedSpeakers?.length >= 100
                                            ? "99+"
                                            : selectedSpeakers?.length}
                                    </FilterCounter>
                                )}
                                <FilterIcon
                                    selected={selectedSpeakers?.length > 0}
                                    icon={["fas", "filter"]}
                                    onClick={() => {
                                        onSearchFieldClick &&
                                            onSearchFieldClick();
                                        toogleFilterDialog();
                                        resetPagination && resetPagination();
                                    }}
                                />
                            </SearchItem>
                            <AppFiltersDialog
                                showTitle
                                open={filterDialogOpen}
                                onClose={toogleFilterDialog}
                                titleText="Add Filters"
                                selectedSpeakers={selectedSpeakers}
                            >
                                <Grid container>
                                    <Grid item xs={12}>
                                        <AppMultiTagSelect
                                            handleChange={onToggleSpeakerSelect}
                                            optionList={uniqueSpeakers}
                                            optionKey="speakerName"
                                            value={selectedSpeakers}
                                            label="Search Speaker"
                                            placeholder="Search Speaker"
                                            handleKeyPress={handleKeyPress}
                                        />
                                    </Grid>
                                    <BtnClearWrapper item xs={12}>
                                        {handleClearFilters && (
                                            <SpeakerFilterBtn
                                                onClick={() => {
                                                    handleClearFilters();
                                                }}
                                                style={{ marginRight: "20px" }}
                                                buttonVariant="primary-filled"
                                            >
                                                Clear Filters
                                            </SpeakerFilterBtn>
                                        )}

                                        <SpeakerFilterBtn
                                            onClick={() => {
                                                toogleFilterDialog();
                                            }}
                                            buttonVariant="primary-filled"
                                        >
                                            Done
                                        </SpeakerFilterBtn>
                                    </BtnClearWrapper>
                                </Grid>
                            </AppFiltersDialog>
                        </>
                    )}
                </>
            )}
        </Grid>
    );
};

const useHeaderHandlers = () => {
    const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);

    const toogleFilterDialog = () => {
        setFilterDialogOpen(!filterDialogOpen);
    };

    return {
        filterDialogOpen,
        toogleFilterDialog,
    };
};
